import React, { useState, useReducer, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import { Modal } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import WorkflowListBase from './Workflow/WorkflowList'
import Workflow from './Workflow'
import BootstrapAlert from '../element/BootstrapAlert'
import ErrorBoundary from '../ErrorBoundary'

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  paper: {
    position: 'absolute',
    width: 600,
    backgroundColor: theme.palette.background.paper,
    border: '0px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3)
  }
}))

const getSelectionState = (selectedId, workflowMap, userMap, firstApprovers) => {
  const firstMembers = [firstApprovers.map((spUser) => userMap.get(spUser.id))]
  if (selectedId) {
    const workflow = workflowMap.get(selectedId)
    const approvers = workflow.approvers
    const levelMap = approvers
      .reduce((m, current, i, src) => {
        if (!m.has(current.level)) {
          m.set(current.level, [])
        }
        m.get(current.level).push(userMap.get(current.sp_user_id))
        return m
      }, new Map())
    return {
      id: selectedId,
      workflow: workflow,
      members: firstMembers.concat(Array.from(levelMap.keys()).sort().map((key) => levelMap.get(key)))
    }
  } else {
    return {
      id: null,
      workflow: null,
      members: firstMembers
    }
  }
}

const selectionStateReducer = (workflowMap, userMap, firstApprovers) => (state, selectedId) => {
  if (state.id === selectedId) {
    return state
  }
  return getSelectionState(selectedId, workflowMap, userMap, firstApprovers)
}

export default function WorkflowSetter (props) {
  const classes = useStyles()
  const [templateMode, setTemplateMode] = useState(true)
  const workflowMapRef = useRef(
    new Map(props.workflows.map((wf) => [wf.id, wf]))
  )
  const userMapRef = useRef(
    new Map(props.generalUsers.map((user) => [user.id, user]).concat(props.managers.map((user) => [user.id, user])))
  )
  const [selectionState, selectionStateDispatcher] = useReducer(
    selectionStateReducer(workflowMapRef.current, userMapRef.current, props.firstApprovers),
    getSelectionState(props.workflows.length > 0 ? props.workflows[0].id : null, workflowMapRef.current, userMapRef.current, props.firstApprovers)
  )
  const [invalidMessage, setInvalidMessage] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    document.getElementById(props.submitModalButtonId)?.addEventListener('click', () => setModalOpen(true))
  }, [props.submitModalButtonId])

  function onTemplateSwitchClick (b) {
    return (e) => {
      setTemplateMode(b)
    }
  }

  return (
    <>
      <BootstrapAlert class="info">
        <div>
          よく使う承認ルートを利用する場合は「テンプレート選択」から設定できます。<br/>
          承認者の追加や変更が必要な場合は、選択後に左上の「テンプレートを基に設定」から編集してください。<br/>
          <b>最初の承認者は自動的に申請の担当者になります。最初の承認者を変更したい場合は申請の担当者を変更してください。</b><br/>
          <b>{props.allowGeneralsOnly ? '※この申請は管理メンバー以外による最終承認が可能です。' : '※必ず管理メンバーを承認ルートに追加してください。'}</b>
        </div>
      </BootstrapAlert>
      <div className="btn-group btn-group-toggle" data-toggle="buttons">
        <label className={'btn btn-primary ' + (templateMode ? 'active' : '')}>
          <input type="radio" name="tmp-switch" value="on" id="temp-switch-on" autoComplete="off" checked={templateMode} onChange={onTemplateSwitchClick(true)} />
          テンプレート選択
        </label>
        <label className={'btn btn-primary ' + (!templateMode ? 'active' : '')}>
          <input type="radio" name="tmp-switch" value="off" id="temp-switch-off" autoComplete="off" checked={!templateMode} onChange={onTemplateSwitchClick(false)} />
          テンプレートを基に設定
        </label>
      </div>
      <div className="row mt-3">
        <div className="col-4 text-center">
          {
            Workflow(
              props.generalUsers,
              props.managers,
              selectionState.members,
              'ライセンシー',
              templateMode,
              props.allowGeneralsOnly,
              null,
              setInvalidMessage,
              null,
              true
            )
          }
        </div>
        <div className="col-8">
          <div className={ templateMode ? '' : 'd-none'}>
            { WorkflowListBase(props.workflows, selectionStateDispatcher) }
          </div>
        </div>
      </div>
      {/* ---- 設定確認モーダル ---- */}
      <Modal
        className={classes.modal}
        open={modalOpen}
        onClose={() => setModalOpen(false)}>
        <div className={classes.paper}>
        {invalidMessage
          ? (
              <>
                <h2>承認ルート設定エラー</h2>
                <hr/>
                <BootstrapAlert class='danger'>
                  <div>{invalidMessage}</div>
                </BootstrapAlert>
              </>
            )
          : (
              <>
                <h2>承認ルート設定確認</h2>
                <hr/>
                <ul className='my-3'>
                  <li>この申請に承認ルートを設定します。</li>
                  <li>承認は行わず、この後に申請を修正できます。</li>
                </ul>
              </>
            )
            }
          <hr />
          <div className="row">
            <div className="col-6">
              <button type='button' className='btn btn-secondary' onClick={() => setModalOpen(false)}>
                キャンセル
              </button>
            </div>
            <div className="col-6 text-right">
              {!invalidMessage && (
                <button type='submit' form={props.formId} className='btn bg-8'>
                  承認ルート確定
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

WorkflowSetter.propTypes = {
  myself: PropTypes.object,
  generalUsers: PropTypes.arrayOf(PropTypes.object),
  managers: PropTypes.arrayOf(PropTypes.object),
  workflows: PropTypes.arrayOf(PropTypes.object),
  allowGeneralsOnly: PropTypes.bool,
  submitModalButtonId: PropTypes.string,
  formId: PropTypes.string,
  firstApprovers: PropTypes.arrayOf(PropTypes.object)
}

WorkflowSetter.defaultProps = {
  allowGeneralsOnly: false,
  submitModalButtonId: 'submit-modal',
  formId: 'main-form'
}

$('#workflow-setter').each((i, el) => {
  const qel = $(el)
  const props = qel.data() // bladeでdata-xxxx形式で指定した値がpropsとして参照できる
  ReactDOM.render(
    <ErrorBoundary>
      <WorkflowSetter {...props}/>
    </ErrorBoundary>,
    el
  )

  Object.keys(props).map((key) => { // ソースにdata-属性が残らないように削除
    qel.removeAttr('data-' + key.replace(/[A-Z]/g, (s) => '-' + s.charAt(0).toLowerCase()))
    return 0
  })
})
